import { MATERIALS, type FinCrossSection, type Issue, type MotorMountMark, type MountTube, type NoseShape, type Placement, type Rocket } from '@openrocket-web/physics';
import type { Motor } from '@openrocket-web/physics';
import { KIND_LABEL, findComponent, type AnyComponent } from '../state/editor';
import type { Action } from '../state/app';
import { findMotor } from '../state/motors';
import { CheckField, NumberField, SelectField, TextField } from './Fields';
import type { Fmt } from './format';

interface Props {
  readonly rocket: Rocket;
  readonly selectedId: string | undefined;
  readonly issues: readonly Issue[];
  readonly motors: readonly Motor[];
  readonly fmt: Fmt;
  readonly dispatch: (a: Action) => void;
}

const SHAPES: ReadonlyArray<{ value: NoseShape; label: string }> = [
  { value: 'ogive', label: 'Ojiva tangente' },
  { value: 'conical', label: 'Cónica' },
  { value: 'elliptical', label: 'Elíptica' },
  { value: 'parabolic', label: 'Parabólica' },
  { value: 'power', label: 'Serie de potencia' },
];
const SECTIONS: ReadonlyArray<{ value: FinCrossSection; label: string }> = [
  { value: 'rounded', label: 'Redondeada' },
  { value: 'square', label: 'Cuadrada (borde plano)' },
  { value: 'airfoil', label: 'Con perfil' },
];
const ANCHORS: ReadonlyArray<{ value: Placement['anchor']; label: string }> = [
  { value: 'front', label: 'Desde el extremo delantero del cuerpo' },
  { value: 'aft', label: 'Desde el extremo trasero del cuerpo' },
];
const EVENTS: ReadonlyArray<{ value: 'ejection' | 'apogee' | 'altitude'; label: string }> = [
  { value: 'ejection', label: 'Carga de eyección del motor' },
  { value: 'apogee', label: 'En el apogeo' },
  { value: 'altitude', label: 'A una altitud (en descenso)' },
];

const materialOptions = (kind: 'bulk' | 'surface' | 'line'): Array<{ value: string; label: string }> =>
  [...MATERIALS.values()].filter((m) => m.kind === kind).map((m) => ({ value: m.id, label: m.name }));

export function PropertyPanel({ rocket, selectedId, issues, motors, fmt, dispatch }: Props): JSX.Element {
  const c: AnyComponent | undefined = findComponent(rocket, selectedId);
  const set = (patch: Record<string, unknown>): void => dispatch({ type: 'update', id: c!.id, patch });
  const err = (field: string): string | undefined => issues.find((i) => i.componentId === c?.id && i.severity === 'error' && i.field === field)?.message;
  const warn = c === undefined ? [] : issues.filter((i) => i.componentId === c.id && i.severity === 'warning');

  const len = (label: string, key: string, value: number): JSX.Element => <NumberField key={key} label={label} unit={fmt.dim} value={value} error={err(key)} onCommit={(v) => set({ [key]: v })} />;
  const material = (kind: 'bulk' | 'surface' | 'line'): JSX.Element => (
    <SelectField label="Material" value={(c as { material: string }).material} options={materialOptions(kind).some((o) => o.value === (c as { material: string }).material) ? materialOptions(kind) : [...materialOptions(kind), { value: (c as { material: string }).material, label: `Personalizado (${(c as { material: string }).material})` }]} error={err('material')} onCommit={(v) => set({ material: v })} />
  );
  const placement = (p: Placement): JSX.Element => (
    <>
      <SelectField label="Posición" value={p.anchor} options={ANCHORS} onCommit={(anchor) => set({ position: { ...p, anchor } })} />
      <NumberField label="Desplazamiento" unit={fmt.dim} value={p.offset} error={err('position')} onCommit={(offset) => set({ position: { ...p, offset } })} />
    </>
  );
  const massOverride = (value: number | undefined): JSX.Element => (
    <NumberField label="Masa explícita (opcional)" unit={fmt.mass} value={value} hint="Sustituye a la calculada con el material" error={err('massOverride')} onCommit={(v) => set({ massOverride: v })} onClear={() => set({ massOverride: undefined })} />
  );

  return (
    <section aria-labelledby="props-title" className="panel">
      <h2 id="props-title">Propiedades</h2>
      {c === undefined ? (
        <>
          <TextField label="Nombre del cohete" value={rocket.name} onCommit={(name) => dispatch({ type: 'rename', name })} />
          <p className="muted">Selecciona un componente de la lista para editarlo.</p>
        </>
      ) : (
        <div key={c.id}>
          <p className="muted small">{KIND_LABEL[c.type]}</p>
          <TextField label="Nombre" value={c.name} onCommit={(name) => set({ name })} />

          {c.type === 'nose-cone' && (
            <>
              <SelectField label="Forma" value={c.shape} options={SHAPES} onCommit={(shape) => set({ shape })} />
              {c.shape === 'power' && <NumberField label="Exponente (0 a 1)" value={c.powerExponent ?? 0.5} error={err('powerExponent')} onCommit={(v) => set({ powerExponent: v })} />}
              {len('Longitud', 'length', c.length)}
              {len('Diámetro de la base', 'baseDiameter', c.baseDiameter)}
              {len('Espesor de pared', 'wallThickness', c.wallThickness)}
              <CheckField label="Macizo" checked={c.filled} onCommit={(filled) => set({ filled })} />
              <CheckField label="Hombro" checked={c.shoulder !== undefined} onCommit={(on) => set({ shoulder: on ? { length: 0.02, diameter: Math.max(c.baseDiameter - 2 * c.wallThickness, c.baseDiameter / 2), thickness: c.wallThickness, capped: false } : undefined })} />
              {c.shoulder !== undefined && (
                <>
                  <NumberField label="Longitud del hombro" unit={fmt.dim} value={c.shoulder.length} error={err('shoulder')} onCommit={(v) => set({ shoulder: { ...c.shoulder!, length: v } })} />
                  <NumberField label="Diámetro del hombro" unit={fmt.dim} value={c.shoulder.diameter} error={err('shoulder')} onCommit={(v) => set({ shoulder: { ...c.shoulder!, diameter: v } })} />
                  <NumberField label="Espesor del hombro" unit={fmt.dim} value={c.shoulder.thickness} error={err('shoulder')} onCommit={(v) => set({ shoulder: { ...c.shoulder!, thickness: v } })} />
                  <CheckField label="Hombro con tapa" checked={c.shoulder.capped} onCommit={(capped) => set({ shoulder: { ...c.shoulder!, capped } })} />
                </>
              )}
              {material('bulk')}
              {massOverride(c.massOverride)}
            </>
          )}

          {c.type === 'body-tube' && (
            <>
              {len('Longitud', 'length', c.length)}
              {len('Diámetro exterior', 'outerDiameter', c.outerDiameter)}
              {len('Espesor de pared', 'wallThickness', c.wallThickness)}
              {material('bulk')}
              {massOverride(c.massOverride)}
              <MountFields tube={c} motors={motors} fmt={fmt} err={err} set={set} dispatch={dispatch} />
            </>
          )}

          {c.type === 'transition' && (
            <>
              {len('Longitud', 'length', c.length)}
              {len('Diámetro delantero', 'foreDiameter', c.foreDiameter)}
              {len('Diámetro trasero', 'aftDiameter', c.aftDiameter)}
              {len('Espesor de pared', 'wallThickness', c.wallThickness)}
              {material('bulk')}
              {massOverride(c.massOverride)}
            </>
          )}

          {c.type === 'fin-set' && (
            <>
              <NumberField label="Número de aletas (3 a 8)" integer value={c.finCount} error={err('finCount')} onCommit={(v) => set({ finCount: v })} />
              {len('Cuerda de raíz', 'rootChord', c.rootChord)}
              {len('Cuerda de punta', 'tipChord', c.tipChord)}
              {len('Envergadura', 'span', c.span)}
              {len('Flecha', 'sweep', c.sweep)}
              {len('Espesor', 'thickness', c.thickness)}
              <SelectField label="Sección" value={c.crossSection ?? 'rounded'} options={SECTIONS} error={err('crossSection')} onCommit={(crossSection) => set({ crossSection })} />
              {material('bulk')}
              {placement(c.position)}
              {massOverride(c.massOverride)}
            </>
          )}

          {c.type === 'inner-tube' && (
            <>
              {len('Longitud', 'length', c.length)}
              {len('Diámetro interior', 'innerDiameter', c.innerDiameter)}
              {len('Espesor de pared', 'wallThickness', c.wallThickness)}
              {material('bulk')}
              {placement(c.position)}
              {massOverride(c.massOverride)}
              <MountFields tube={c} motors={motors} fmt={fmt} err={err} set={set} dispatch={dispatch} />
            </>
          )}

          {c.type === 'parachute' && (
            <>
              {len('Diámetro', 'diameter', c.diameter)}
              <NumberField label="Coeficiente de arrastre" value={c.dragCoefficient} error={err('dragCoefficient')} onCommit={(v) => set({ dragCoefficient: v })} />
              {len('Longitud empacada', 'packedLength', c.packedLength)}
              <SelectField label="Despliegue" value={c.deployment.event} options={EVENTS} onCommit={(event) => set({ deployment: event === 'altitude' ? { event, altitude: 100 } : { event } })} />
              {c.deployment.event === 'altitude' && <NumberField label="Altitud de despliegue" unit={fmt.dist} value={c.deployment.altitude} error={err('deployment')} onCommit={(altitude) => set({ deployment: { event: 'altitude', altitude } })} />}
              {material('surface')}
              <CheckField label="Cuerdas" checked={c.lines !== undefined} onCommit={(on) => set({ lines: on ? { count: 6, length: c.diameter, material: 'nylon-cord' } : undefined })} />
              {c.lines !== undefined && (
                <>
                  <NumberField label="Número de cuerdas" integer value={c.lines.count} error={err('lines')} onCommit={(v) => set({ lines: { ...c.lines!, count: v } })} />
                  <NumberField label="Longitud de cada cuerda" unit={fmt.dim} value={c.lines.length} error={err('lines')} onCommit={(v) => set({ lines: { ...c.lines!, length: v } })} />
                  <SelectField label="Material de las cuerdas" value={c.lines.material} options={materialOptions('line').some((o) => o.value === c.lines!.material) ? materialOptions('line') : [...materialOptions('line'), { value: c.lines.material, label: `Personalizado (${c.lines.material})` }]} onCommit={(v) => set({ lines: { ...c.lines!, material: v } })} />
                </>
              )}
              {placement(c.position)}
              {massOverride(c.massOverride)}
            </>
          )}

          {c.type === 'shock-cord' && (
            <>
              {len('Longitud empacada', 'length', c.length)}
              {len('Longitud del cordón', 'cordLength', c.cordLength)}
              {material('line')}
              {placement(c.position)}
              {massOverride(c.massOverride)}
            </>
          )}

          {c.type === 'mass-component' && (
            <>
              <NumberField label="Masa" unit={fmt.mass} value={c.mass} error={err('mass')} onCommit={(v) => set({ mass: v })} />
              {len('Longitud', 'length', c.length)}
              {placement(c.position)}
            </>
          )}

          {(c.type === 'centering-ring' || c.type === 'bulkhead' || c.type === 'engine-block') && (
            <>
              {len('Longitud', 'length', c.length)}
              {len('Diámetro exterior', 'outerDiameter', c.outerDiameter)}
              {len('Diámetro interior', 'innerDiameter', c.innerDiameter)}
              {material('bulk')}
              {placement(c.position)}
              {massOverride(c.massOverride)}
            </>
          )}

          {c.type === 'launch-lug' && (
            <>
              {len('Longitud', 'length', c.length)}
              {len('Diámetro exterior', 'outerDiameter', c.outerDiameter)}
              {len('Espesor de pared', 'wallThickness', c.wallThickness)}
              {material('bulk')}
              {placement(c.position)}
              {massOverride(c.massOverride)}
            </>
          )}

          {warn.length > 0 && (
            <ul className="issue-list issue-list--warning" aria-label="Avisos de este componente">
              {warn.map((w, i) => (
                <li key={i}>{w.message}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

interface MountProps {
  readonly tube: MountTube;
  readonly motors: readonly Motor[];
  readonly fmt: Fmt;
  readonly err: (f: string) => string | undefined;
  readonly set: (patch: Record<string, unknown>) => void;
  readonly dispatch: (a: Action) => void;
}

/** Marca de portamotor de un tubo (de cuerpo o interior) y, si la lleva, su voladizo y su motor. */
function MountFields({ tube, motors, fmt, err, set, dispatch }: MountProps): JSX.Element {
  const mark: MotorMountMark | undefined = tube.motorMount;
  const check = <CheckField label="Portamotor" checked={mark !== undefined} onCommit={(on) => dispatch({ type: 'mount-set', id: tube.id, on })} />;
  if (mark === undefined) return check;
  const current = mark.motor === undefined ? undefined : findMotor(motors, mark.motor.motorId);
  const missing = mark.motor !== undefined && current === undefined;
  const options = [
    { value: '', label: '(sin motor)' },
    ...(missing ? [{ value: '__missing__', label: `Falta en la biblioteca: ${mark.motor!.motorId}` }] : []),
    ...motors.map((m) => ({ value: m.id, label: `${m.manufacturer} ${m.designation} · clase ${m.impulseClass} · Ø ${fmt.dimension(m.diameter, 3)}` })),
  ];
  const value = mark.motor === undefined ? '' : current !== undefined ? current.id : '__missing__';
  return (
    <>
      {check}
      <NumberField label="Voladizo del motor" unit={fmt.dim} value={mark.overhang ?? 0} hint="Cuánto sobresale el motor por detrás" error={err('overhang')} onCommit={(v) => set({ motorMount: { ...mark, overhang: v } })} />
      <SelectField
        label="Motor"
        value={value}
        options={options}
        error={err('motor')}
        onCommit={(id) => {
          if (id === '') dispatch({ type: 'clear-motor', mountId: tube.id });
          else if (id !== '__missing__') dispatch({ type: 'assign-motor', mountId: tube.id, motorId: id, delay: mark.motor?.ejectionDelay ?? current?.delays[0] ?? 5 });
        }}
      />
      {mark.motor !== undefined && (
        <NumberField
          label="Retardo de eyección (s)"
          value={mark.motor.ejectionDelay}
          hint={current !== undefined && current.delays.length > 0 ? `El motor ofrece: ${current.delays.join(', ')} s` : undefined}
          onCommit={(v) => dispatch({ type: 'assign-motor', mountId: tube.id, motorId: mark.motor!.motorId, delay: v })}
        />
      )}
    </>
  );
}
